import { Button } from '../../../../widgets/buttons';
import { Checkbox } from '../../../../widgets/checkbox';
import { Columns } from '../../../../widgets/columns';
import { FormControl } from '../../../../widgets/formControl';
import { Icon } from '../../../../widgets/icon';
import { Input } from '../../../../widgets/input';
import { MessageDialog, useDialogManager } from '../../../../widgets/dialog';
import { PageFooterButtons } from '../../../../widgets/headers';
import { Radio } from '../../../../widgets/radio';
import { TextArea } from '../../../../widgets/textArea';
import { VerticalLayout } from '../../../../widgets/layouts';
import { ZTooltip } from '../../../../widgets/ztooltip';
import { removeNotification, showNotification } from '../../../../widgets/notificationToaster';
import update from 'immutability-helper';
import React, { FC, FunctionComponent, useEffect, useMemo, useState } from 'react';
import { graphql } from 'react-relay';
import { useFragment, useLazyLoadQuery, useMutation } from 'react-relay/hooks';
import { CustomErrorBoundary } from 'src/components/widgets/customErrorBoundary';
import { Section } from 'src/components/widgets/section';
import { Center, ErrorMessage, ImageContainer, hoverOpacity } from 'src/shared/globals';
import { T, stringFormat } from 'src/shared/helpers';
import MdCheck from 'src/shared/icons/check-solid.svg';
import MdErrorOutline from 'src/shared/icons/exclamation-circle-solid.svg';
import NoImage from 'src/shared/svg/noImage';
import styled from 'styled-components';
import { useProductConfigurationChangeStep, useProductConfigurationState } from '../../state';
import { ProviderRegionStock, productProvider_product$key } from '../../state/queries/__generated__/productProvider_product.graphql';
import { productProviderProduct } from '../../state/queries/productProvider';
import { importConfigurationMutation, importConfigurationMutation$data } from './__generated__/importConfigurationMutation.graphql';
import { importConfigurationQuery } from './__generated__/importConfigurationQuery.graphql';
import {
    ProductProviderProductSide,
    getColors,
    getImportMutationData,
    getLocations,
    getOptions,
    getPrices,
    getSides,
    getSizes,
    getVariants,
    locationsNames
} from './helpers';
import PricesTable from './pricesTable';

const OrientationRadio = styled(Radio)`
    float: none;
    display: block;
`;

const OptionCheckbox = styled(Checkbox)``;

const ColorSquare = styled.div`
    ${hoverOpacity}
    width: 32px;
    height: 32px;
    border: 1px #ddd solid;
    margin-right: 10px;
    margin-bottom: 10px;
    background-color: ${(props) => props.color};
    display: inline-flex !important;
    align-items: center;
    justify-content: center;
    vertical-align: middle;

    ${Icon} {
        width: 24px;
        height: 24px;
        stroke: rgba(0, 0, 0, 0.25);
        color: white;
    }
`;

const Color: FC<{ colorCode: string; colorName: string; checked: boolean; onClick: () => void }> = ({ checked, colorCode, colorName, onClick }) => {
    return (
        <ColorSquare color={colorCode} onClick={onClick}>
            {checked && (
                <ZTooltip html={<>{colorName}</>}>
                    <Icon>
                        <MdCheck />
                    </Icon>
                </ZTooltip>
            )}
        </ColorSquare>
    );
};

const SideContainer = styled.div<{ $selected: boolean; disabled: boolean }>`
    border: 1px transparent solid;
    padding: 10px;

    span {
        text-align: center;
        display: block;
        margin-top: 5px;
    }

    ${(props) =>
        props.$selected &&
        `
        border-color: ${props.theme.app.primary};
    `}

    ${(props) =>
        !props.disabled &&
        `
        ${ImageContainer} {
            ${hoverOpacity()}
        }`}

    ${(props) =>
        props.disabled &&
        `
        opacity: 0.7;
    `}
`;

const ConflictSideContainer = styled(ImageContainer)`
    color: ${(props) => props.theme.app.primary};
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 5px;

    ${Icon} {
        margin-bottom: 10px;
    }
`;

const Side: FC<{
    selected: boolean;
    side: ProductProviderProductSide;
    conflictingSides: ProductProviderProductSide[];
    onClick: () => void;
}> = ({ selected, side, conflictingSides, onClick }) => {
    return (
        <SideContainer
            $selected={selected}
            disabled={conflictingSides.length > 0}
            onClick={() => {
                if (conflictingSides.length == 0) onClick();
            }}
        >
            {conflictingSides.length == 0 && <ImageContainer>{side.imageUrl ? <img src={side.imageUrl}></img> : <NoImage />}</ImageContainer>}

            {conflictingSides.length > 0 && (
                <ConflictSideContainer>
                    <Icon>
                        <MdErrorOutline />
                    </Icon>
                    {stringFormat(T._(`Can't be combined with {0}`, 'Admin'), conflictingSides[0].name)}
                </ConflictSideContainer>
            )}

            <span>{side.name}</span>
        </SideContainer>
    );
};

const ImportConfigurationInner: FunctionComponent<{}> = (props) => {
    const { state } = useProductConfigurationState();
    const changeStep = useProductConfigurationChangeStep();

    const query = useLazyLoadQuery<importConfigurationQuery>(
        graphql`
            query importConfigurationQuery($productID: String!, $provider: ProductProvider!) {
                providerProduct(productID: $productID, provider: $provider) {
                    ...productProvider_product
                }
                ecommerceSettings {
                    ecommerceName
                    currencyCode
                }
            }
        `,
        {
            productID: state.providerProduct?.id ?? '',
            provider: state.provider
        }
    );

    const product = useFragment<productProvider_product$key>(productProviderProduct, query.providerProduct);

    if (!product) throw new Error('Product not found');

    const [importProduct, isImportingProduct] = useMutation<importConfigurationMutation>(graphql`
        mutation importConfigurationMutation($input: ProductProviderInput!, $provider: ProductProvider!) {
            importProviderProduct(input: $input, provider: $provider) {
                taskID
            }
        }
    `);

    const { showDialog } = useDialogManager();
    const [selectedLocations, setSelectedLocations] = useState<ProviderRegionStock[]>([]);
    const [selectedOptions, setSelectedOptions] = useState<[string, string][]>([]);
    const [selectedColors, setSelectedColors] = useState<string[]>([]);
    const [selectedSizes, setSelectedSizes] = useState<string[]>([]);
    const [selectedSides, setSelectedSides] = useState<string[]>([]);
    const [name, setName] = useState(product.generalInfo.name);
    const [description, setDescription] = useState(product.generalInfo.description);
    const [profitString, setProfitString] = useState('0');
    const profit = useMemo(() => parseFloat(profitString) || 0, [profitString]);

    const options = useMemo(() => getOptions(product), [product]);
    const sizes = useMemo(() => getSizes(product, selectedLocations, state.provider), [product, selectedLocations]);
    const colors = useMemo(() => getColors(product, selectedLocations, state.provider), [product, selectedLocations]);
    const locations = useMemo(() => getLocations(product), [product]);
    const sides = useMemo(() => getSides(product), [product]);
    const selectedVariants = useMemo(() => getVariants(product, selectedColors, selectedSizes), [product, selectedColors, selectedSizes]);
    const prices = useMemo(() => getPrices(selectedVariants, profit), [product, selectedVariants, profit]);

    // First options and locations selection
    useEffect(() => {
        setSelectedLocations(locations);
        setSelectedOptions(options.map((x) => [x.optionID, x.values[0] ?? '']));
    }, [product, locations]);

    // Select the only available elements on locations changes
    useEffect(() => {
        if (sizes.length == 1) {
            setSelectedSizes(sizes);
        } else {
            // De-select sizes no more available in the selected locations
            let newSelectedSizes = selectedSizes.filter((x) => sizes.indexOf(x) !== -1);
            if (newSelectedSizes.length != selectedSizes.length) setSelectedSizes(newSelectedSizes);
        }

        if (colors.length == 1) {
            setSelectedColors(colors.map((x) => x.name));
        } else {
            // De-select colors no more available in the selected locations
            let newSelectedColors = selectedColors.filter((x) => colors.findIndex((c) => c.name == x) !== -1);
            if (newSelectedColors.length != selectedColors.length) setSelectedColors(newSelectedColors);
        }

        if (sides.length == 1) setSelectedSides(sides.map((x) => x.id));
    }, [locations, selectedLocations]);

    // Selection
    const handleLocationChange = (location: ProviderRegionStock) => {
        let locations = [...selectedLocations];

        if (locations.indexOf(location) === -1) locations = [...locations, location];
        else locations = locations.filter((x) => x != location);

        setSelectedLocations(locations);
    };

    const handleOptionChange = (optionId: string, value: string) => {
        let options = [...selectedOptions];
        let index = options.findIndex((o) => o[0] === optionId);

        if (index == -1) options = [...options, [optionId, value]];
        else
            options = update(options, {
                [index]: {
                    $set: [optionId, value]
                }
            });

        setSelectedOptions(options);
    };

    const handleSizeChange = (size: string) => {
        let sizes = [...selectedSizes];

        if (sizes.indexOf(size) === -1) sizes = [...sizes, size];
        else sizes = sizes.filter((x) => x != size);

        setSelectedSizes(sizes);
    };

    const handleColorChange = (colorName: string) => {
        let colors = [...selectedColors];

        if (colors.indexOf(colorName) === -1) colors = [...colors, colorName];
        else colors = colors.filter((x) => x != colorName);

        setSelectedColors(colors);
    };

    const handleSideChange = (sideId: string) => {
        let sides = [...selectedSides];

        if (sides.indexOf(sideId) === -1) sides = [...sides, sideId];
        else sides = sides.filter((x) => x != sideId);

        setSelectedSides(sides);
    };

    const handleSaveClick = async () => {
        const variantsCount = selectedSizes.length * selectedColors.length;
        let errorMessage: string | null = null;

        if (query.ecommerceSettings?.ecommerceName == 'SHOPIFY' && variantsCount > 100) {
            errorMessage = stringFormat(
                T._("You've exceeded you store's variants limit per product.<br/>You selected {0}, but the limit is 100 variants.", 'Admin'),
                variantsCount.toString()
            );
        }

        if (sides.length != 1 && selectedSides.length == 0) {
            errorMessage = T._('Select at last one side.', 'Admin');
        }

        if (selectedVariants.length == 0) {
            errorMessage = T._('Select at last one variant.', 'Admin');
        }

        if (errorMessage) {
            showDialog('variants-limit', <MessageDialog message={<span dangerouslySetInnerHTML={{ __html: errorMessage }} />} />);

            return;
        }

        const input = getImportMutationData(product, name, description, selectedOptions, selectedVariants, selectedSides, profit);

        const loadingNotification = showNotification({
            type: 'default',
            title: T._('Saving...', 'Admin'),
            message: T._('Adding product creation to queue...', 'Admin')
        });

        await new Promise<importConfigurationMutation$data>((resolve, reject) =>
            importProduct({
                onCompleted: resolve,
                onError: reject,
                variables: {
                    input,
                    provider: state.provider
                }
            })
        );

        changeStep('pod-done');

        removeNotification(loadingNotification);
        showNotification({
            type: 'success',
            message:
                state.provider == 'PRINTFUL'
                    ? T._("We'll inform you as soon as the product is created in your store", 'Admin')
                    : T._('Product added to the queue.', 'Admin'),
            dismiss: {
                duration: 5000
            }
        });
    };

    if (sides.length == 0) throw new Error('No sides');

    return (
        <React.Fragment>
            <VerticalLayout>
                <Section title={T._('Product info', 'Admin')}>
                    <FormControl label={T._('Title', 'Admin')}>
                        <Input value={name} onChange={(e) => setName(e.currentTarget.value)} />
                    </FormControl>
                    <FormControl label={T._('Description', 'Admin')}>
                        <TextArea
                            value={description}
                            onChange={(e) => setDescription(e.currentTarget.value)}
                            css={`
                                height: 150px;
                            `}
                        />
                    </FormControl>
                </Section>
                <Section title={T._('Options', 'Admin')}>
                    {/* Locations */}
                    {locations.length > 0 && (
                        <FormControl label={T._('Fulfilled in', 'Admin')}>
                            <Columns $xs={4} $md={6} $lg={6}>
                                {locations.map((location) => (
                                    <OptionCheckbox
                                        key={location}
                                        checked={selectedLocations.indexOf(location) !== -1}
                                        onChange={() => handleLocationChange(location)}
                                        label={locationsNames[location as string]}
                                    />
                                ))}
                            </Columns>
                        </FormControl>
                    )}

                    {/* Options */}
                    {options.length > 0 &&
                        options.map((option) => (
                            <FormControl key={option.optionID} label={option.title}>
                                {option.values.map((value) => (
                                    <OrientationRadio
                                        checked={selectedOptions.some((selected) => selected[0] == option.optionID && selected[1] == value)}
                                        name={option.optionID}
                                        value={value || ''}
                                        label={value || ''}
                                        onSelect={() => handleOptionChange(option.optionID, value ?? '')}
                                    />
                                ))}
                            </FormControl>
                        ))}

                    {/* Sizes */}
                    {sizes.length > 0 && (
                        <FormControl label={T._('Choose the sizes available', 'Admin')}>
                            {/*<SelectAllCheckbox checked onChange={() => null} label={T._('Select all sizes', 'Admin')} />*/}

                            <Columns $xs={4} $md={sizes[0].length > 5 ? 7 : 15} $lg={sizes[0].length > 5 ? 7 : 15}>
                                {sizes.map((size) => (
                                    <OptionCheckbox
                                        key={size}
                                        checked={selectedSizes.indexOf(size) !== -1}
                                        onChange={() => handleSizeChange(size)}
                                        label={size}
                                    />
                                ))}
                            </Columns>
                        </FormControl>
                    )}

                    {/* Colors */}
                    {colors.length > 0 && (
                        <FormControl label={T._('Choose the colors available', 'Admin')}>
                            {/*<SelectAllCheckbox checked onChange={() => null} label={T._('Select all colors', 'Admin')} />*/}

                            {colors.map((color) => (
                                <Color
                                    key={color.name}
                                    colorCode={color.code}
                                    colorName={color.name}
                                    checked={selectedColors.indexOf(color.name) !== -1}
                                    onClick={() => handleColorChange(color.name)}
                                />
                            ))}
                        </FormControl>
                    )}
                </Section>

                {sides.length > 1 && (
                    <Section title={T._(`Choose the sides to import`, 'Admin')}>
                        <Columns $xs={2} $md={5} $lg={5}>
                            {sides.map((side) => (
                                <Side
                                    key={side.name}
                                    side={side}
                                    conflictingSides={sides.filter((x) => selectedSides.indexOf(x.id) !== -1 && x.conflicts.indexOf(side.id) !== -1)}
                                    selected={selectedSides.indexOf(side.id) !== -1}
                                    onClick={() => handleSideChange(side.id)}
                                />
                            ))}
                        </Columns>
                    </Section>
                )}

                <Section title={T._(`Choose the mark up price and we'll auto configure your product prices`, 'Admin')}>
                    <PricesTable prices={prices} profit={profitString} onProfitChange={setProfitString} provider={state.provider} />
                </Section>
            </VerticalLayout>

            <PageFooterButtons $fixed={true}>
                <Button disabled={isImportingProduct} onClick={handleSaveClick}>
                    {T._('Save product', 'Admin')}
                </Button>
            </PageFooterButtons>
        </React.Fragment>
    );
};

export const ImportConfiguration: FC<{}> = () => {
    return (
        <CustomErrorBoundary
            render={(e) => (
                <Center>
                    <ErrorMessage>{T._('Failed reading the product. Please contact us at help@zakeke.com', 'Admin')}</ErrorMessage>
                </Center>
            )}
        >
            <ImportConfigurationInner />
        </CustomErrorBoundary>
    );
};
